import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import SpaceScene from '../components/three/SpaceScene';
import HeroOverlay from '../components/HeroOverlay';
import Telemetry from '../components/Telemetry';
import Marquee from '../components/Marquee';
import MapSection from '../components/map/MapSection';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import Reveal, { SectionHeader } from '../components/Reveal';
import { scrollProgress, transitionProgress } from '../lib/atlas';

gsap.registerPlugin(ScrollTrigger);

function useIsMobile() {
  const [mobile, setMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    const onChange = (e) => setMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return mobile;
}

export default function Home() {
  const introRef = useRef(null);
  const transRef = useRef(null);
  const [activeIntro, setActiveIntro] = useState(true);
  const [activeTrans, setActiveTrans] = useState(false);
  const reducedMotion = useReducedMotion();
  const isMobile = useIsMobile();

  const { scrollYProgress } = useScroll({
    target: introRef,
    offset: ['start start', 'end end'],
  });

  const { scrollYProgress: transY } = useScroll({
    target: transRef,
    offset: ['start end', 'end end'],
  });

  const heroOpacity = useTransform(scrollYProgress, [0, 0.22], [1, 0]);
  const heroY = useTransform(scrollYProgress, [0, 0.22], [0, -60]);

  // Veil + caption for the Earth-to-map handoff
  const veilOpacity = useTransform(transY, [0.06, 0.38], [0, 1]);
  const captionOpacity = useTransform(transY, [0.09, 0.19, 0.32], [0, 1, 0]);
  const captionY = useTransform(transY, [0.09, 0.32], [12, -12]);

  useEffect(() => {
    if (reducedMotion) {
      scrollProgress.value = 0.82;
      transitionProgress.value = 0;
      return;
    }
    const t1 = ScrollTrigger.create({
      trigger: introRef.current,
      start: 'top top',
      end: 'bottom bottom',
      onUpdate: (self) => {
        scrollProgress.value = self.progress;
      },
      onToggle: (self) => setActiveIntro(self.isActive),
    });
    const t2 = ScrollTrigger.create({
      trigger: transRef.current,
      start: 'top bottom',
      end: 'bottom bottom',
      onUpdate: (self) => {
        transitionProgress.value = self.progress;
      },
      onToggle: (self) => setActiveTrans(self.isActive),
    });
    return () => {
      t1.kill();
      t2.kill();
    };
  }, [reducedMotion]);

  return (
    <div className="grain">
      <Nav />
      <SpaceScene
        mobile={isMobile}
        active={activeIntro || activeTrans}
        reducedMotion={reducedMotion}
      />
      <HeroOverlay heroOpacity={heroOpacity} heroY={heroY} />
      <Telemetry progress={scrollYProgress} opacity={heroOpacity} />

      {/* Veil that crossfades the receding planet into the map chapter */}
      <motion.div
        style={{ opacity: veilOpacity }}
        className="fixed inset-0 z-[5] pointer-events-none bg-void"
        data-testid="orbit-veil"
      />
      <motion.div
        style={{ opacity: captionOpacity, y: captionY }}
        className="fixed inset-x-0 top-1/2 z-[6] pointer-events-none text-center px-6"
        data-testid="orbit-transition-caption"
      >
        <p className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.45em] text-slate-500">
          From orbit to ground level
        </p>
      </motion.div>

      {/* Scroll runway 1 — the approach */}
      <div
        ref={introRef}
        className="relative h-[200vh] pointer-events-none"
        data-testid="intro-track"
      />

      {/* Scroll runway 2 — the pull-back + handoff to the map */}
      <div
        ref={transRef}
        className="relative h-[90vh] pointer-events-none"
        data-testid="transition-track"
      />

      <main className="relative z-10 bg-void">
        <MapSection isMobile={isMobile} reducedMotion={reducedMotion} />

        <Marquee />

        <section
          id="about"
          className="px-6 lg:px-16 py-24 lg:py-36 border-t border-white/[0.06]"
          data-testid="section-about"
        >
          <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <SectionHeader eyebrow="About" title="KARAN SONI" />
              <Reveal delay={0.2}>
                <p className="mt-6 font-serif italic text-xl sm:text-2xl text-violettext leading-snug">
                  Creative Director &amp; Brand Strategist
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 space-y-8">
              <Reveal>
                <p className="text-base sm:text-lg text-slate-300 leading-relaxed">
                  A creative director and brand strategist working at the intersection of human
                  psychology, culture, storytelling, and emerging technology.
                </p>
              </Reveal>
              <Reveal delay={0.08}>
                <p className="text-base sm:text-lg text-slate-400 leading-relaxed">
                  I turn human insight into ideas, content, and brand systems that move people
                  and businesses forward, with over 10 years of experience across India, North
                  America, UAE, and Southeast Asia.
                </p>
              </Reveal>
              <Reveal delay={0.12}>
                <p className="text-sm sm:text-base text-slate-400 leading-relaxed">
                  Experience across Unilever, WPP Media (MediaCom), Culture Machine, and
                  independent consulting.
                </p>
              </Reveal>
              <Reveal delay={0.16}>
                <div className="flex flex-wrap gap-3 pt-2">
                  {['Human Psychology', 'Culture', 'Storytelling', 'Emerging Technology'].map(
                    (tag) => (
                      <span
                        key={tag}
                        className="px-4 py-2 border border-white/10 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 hover:border-ultraviolet/60 hover:text-violettext transition-colors duration-300"
                      >
                        {tag}
                      </span>
                    )
                  )}
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        <section
          id="contact"
          className="px-6 lg:px-16 py-24 lg:py-36 border-t border-white/[0.06]"
          data-testid="section-contact"
        >
          <div className="max-w-[1400px] mx-auto">
            <SectionHeader eyebrow="Contact" title="START A CONVERSATION" />
            <Reveal delay={0.2}>
              <p className="mt-8 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed">
                Available for conversations about ideas, brands, and work across borders.
              </p>
            </Reveal>
            <Reveal delay={0.28}>
              <p
                className="mt-6 font-mono text-[10px] uppercase tracking-[0.3em] text-slate-600"
                data-testid="contact-stage-note"
              >
                Direct line arrives with Stage Two — the Atlas is under expansion
              </p>
            </Reveal>
          </div>
        </section>

        <Footer />
      </main>
    </div>
  );
}
